import { reactive, ref } from 'vue'
import { defineStore } from 'pinia'
import InsuranceService from '@/services/InsuranceService'
import ProductService from '@/services/ProductService'
import { useCurrencyStore } from '@/stores/CurrencyStore'
import { notEmpty, formatName } from '@/utils/tool.js'
import { pagelog } from '@/utils/pagelog'
import Request from '@/services/Request'
import { localDriver } from '@/utils/localDriver'
import {
  getMergedCommittedInsuranceCoverage,
  normalizeInsurancePolicyResults
} from '@/utils/insurancePolicyResult'
import { toPrimaryDestination } from '@/utils/insuranceDestination'
import i18n from '@/i18n/index.js'

export const useInsuranceStore = defineStore('insurance', () => {
  let planId = null
  function getHostname() {
    return typeof window !== 'undefined' && window?.location?.hostname != null ? String(window.location.hostname) : ''
  }

  function isProductionInsuranceHost(host: string) {
    const normalized = host.toLowerCase()
    return normalized === 'www.triptogo.com' || normalized === 'triptogo.com'
  }

  function isTestInsuranceHost(host: string){
    const parts = host
      .toLowerCase()
      .split('.')
      .map((p) => p.trim())
      .filter(Boolean)
    return parts.some((p) => p.includes('test'))
  }

  // function getInsurancePlanId() {
  //   const host = getHostname()
  //   if (isProductionInsuranceHost(host)) return 'PROPLUS2025'

  //   const configured = (import.meta as any)?.env?.VITE_INSURANCE_PLAN_ID
  //   if (configured != null && String(configured).trim() !== '') return String(configured).trim()

  //   if (isTestInsuranceHost(host)) return 'PROPLUS2025'

  //   return 'PROPLUS2025'
  // }

  function buildRawQuoteInfoForSubmit(planId: string) {
    const host = getHostname()
    const rawQuoteInfo = {
      customers: quoteInfo.customers,
      totalTripCost: quoteInfo.totalTripCost,
      coverage: quoteInfo.coverage
    } as any

    if (isProductionInsuranceHost(host)) {
      return rawQuoteInfo
    }

    rawQuoteInfo.planId = planId
    return rawQuoteInfo
  }

  function getQuoteInfoCacheKey() {
    return getInsuranceQuoteCacheKey()
  }

  async function removeAllQuoteInfoCache() {
    let cachkey = getQuoteInfoCacheKey()
    await localDriver.removeItem(cachkey)
    // await localDriver.removeItem('insuranceQuoteInfo:PROPLUS2025')
    // await localDriver.removeItem('insuranceQuoteInfo:PROPLUS2025')
  }

  const quoteInfo = reactive({
    planId: planId,
    customers: [],
    totalTripCost: 0,
    sourceTripPrice: null as number | null,
    coverage: {
      effectiveDate: null,
      expirationDate: null,
      destinations: [] as { countryCode: string }[]
    }
  })

  function formatDate(date: Date): string {
    const year = date.getFullYear() // 获取年份
    const month = date.getMonth() + 1 // 获取月份，月份从0开始，所以加1
    const day = date.getDate() // 获取日

    // 将月和日格式化为两位数
    const formattedMonth = month < 10 ? `0${month}` : month.toString()
    const formattedDay = day < 10 ? `0${day}` : day.toString()

    // 构建并返回格式化的日期字符串
    return `${year}-${formattedMonth}-${formattedDay}`
  }

  function calculateAge(birthday) {
    const birthDate = new Date(birthday)
    const today = new Date()

    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDifference = today.getMonth() - birthDate.getMonth()

    // 如果当前月份小于生日月份，或者月份相同但今天的日期小于生日日期，年龄应减1
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }

    return age
  }

  const totalPrice = ref(0)
  const unPay = ref(0)
  // Display-currency copy of unPay (Arch quotes/charges in USD; this is the same total
  // converted for whatever currency the page is showing - identical to unPay when the
  // page currency is already USD, since the backend only adds *Display fields then).
  const unPayDisplay = ref(0)
  // Signed net premium change for the update flow (can be NEGATIVE = the new premium is lower, i.e.
  // a refund/decrease). unPay clamps each person to >=0 (max(0,...)), which hides decreases; netUnPay
  // keeps the sign so a decrease can be committed through payWithInvoice instead of becoming $0.
  const netUnPay = ref(0)
  const paid = ref(0)
  // When true, the booking flow bypasses insurance validation (user chose to skip).
  const skip = ref(false)
  function setSkip(value: boolean) {
    skip.value = value
  }
  function refreshTotals() {
    const accepted = quoteInfo.customers.filter((c) => c.accept == true)
    const costMap = accepted.map((c) => Number(c?.insuranceQuote?.individualPolicyCost ?? 0))
    unPay.value = costMap ? costMap.reduce((acc, val) => acc + val, 0) : unPay.value
    // Convert the USD total once with the backend's unrounded rate when available: summing
    // per-person rounded display amounts can differ by a cent from what is recorded at payment
    // (USD total x rate), e.g. €84.93 + €113.53 = €198.46 vs USD 229 x 0.8667 = €198.47.
    const rates = accepted.map((c) => Number(c?.insuranceQuote?.displayRate ?? 0))
    const displayRate = rates.length && rates.every((r) => r > 0 && r === rates[0]) ? rates[0] : 0
    unPayDisplay.value = displayRate
      ? Math.round(unPay.value * displayRate * 100) / 100
      : accepted.reduce(
          (acc, c) => acc + Number(c?.insuranceQuote?.individualPolicyCostDisplay ?? c?.insuranceQuote?.individualPolicyCost ?? 0),
          0
        )
    // Signed net: falls back to individualPolicyCost when no per-customer net was recorded
    // (normal purchase flow), so netUnPay == unPay there and only the update flow sees negatives.
    netUnPay.value = accepted.reduce(
      (acc, c) => acc + Number(c?.insuranceQuote?.netPolicyCost ?? c?.insuranceQuote?.individualPolicyCost ?? 0),
      0
    )
  }

  function changeAccept(index: number, value: boolean) {
    quoteInfo.customers[index].accept = value
    refreshTotals()
  }
  function declineAll() {
    for (const c of quoteInfo.customers) {
      c.accept = false
    }
    refreshTotals()
  }

  function getCurrentDate() {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date())

    const year = parts.find((part) => part.type === 'year')?.value ?? ''
    const month = parts.find((part) => part.type === 'month')?.value ?? ''
    const day = parts.find((part) => part.type === 'day')?.value ?? ''

    return `${year}-${month}-${day}`
  }

  function buildInsuredFromCustomer(c: any) {
    return {
      uniqueId: c.travelerId,
      firstName: c.firstname,
      lastName: c.lastname,
      middleName: c.middlename,
      dob: c.Birthday??c.birthday,
      age: c.age,
      individualTripCost:
        parseFloat(c.tripPrice) +
        parseFloat(c.soloPrice) +
        parseFloat(c.optionsPrice) +
        parseFloat(c.flightPrice),
      address: {
        countryAbbreviation: c.countryAbbreviation,
        stateAbbreviation: c.stateAbbreviation
      }
    }
  }

  function getCountryIdFromZoneId(zoneId) {
    if (parseInt(zoneId) > 1000) {
      return '223'
    } else {
      return zoneId
    }
  }
  function getStateIdFromZoneId(zoneId) {
    if (parseInt(zoneId) <= 1000) {
      return null
    } else {
      return zoneId
    }
  }
  const hasError = ref(false)
  const quoteErrors = ref<string[]>([])
  function addQuoteError(response?: any) {
    const candidates = [
      response?.msg,
      response?.message,
      response?.error_description,
      response?.error,
      response?.response?.data?.msg,
      response?.response?.data?.message,
      response?.responseMessage
    ]
    const suppliedMessage = candidates.find((value) => typeof value === 'string' && value.trim() !== '')
    const rawMessage = suppliedMessage?.trim() || ''
    const message = /traveler age limit exceeded/i.test(rawMessage)
      ? i18n.global.t('insurance.travelerAgeLimitExceeded')
      : i18n.global.t('insurance.quoteUnavailable')
    if (!quoteErrors.value.includes(message)) {
      quoteErrors.value.push(message)
    }
  }
  function toFiniteNumber(value: any, fallback = 0): number {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) ? numberValue : fallback
  }

  async function getGroupQuote(group: any[], coverage: any,previousInsurance:any) {
    if (group == null || group.length == 0) {
      return
    }
    const displayCurrency = useCurrencyStore().currentCurrency
    try {
      if( (previousInsurance.id??0)>0 )
      {
        const resp = await InsuranceService.updateQuotes({
          returnIndividualTravelerPricingDetails: true,
          insuredPerson: group.map((c) => buildInsuredFromCustomer(c)),
          coverage: coverage
        }, previousInsurance?.order_id ?? 0, previousInsurance.id, previousInsurance.ins_order_number, displayCurrency)
        if (resp?.success != null && resp.success == false) {
          const responseMessage = String(resp?.responseMessage ?? resp?.message ?? '')
          if (!previousInsurance?.id && group.length > 1 && /traveler age limit exceeded/i.test(responseMessage)) {
            await Promise.all(group.map((customer) => getGroupQuote([customer], coverage, {})))
            return
          }
          hasError.value = true
          addQuoteError(resp)
          return
        }

        const quotes = Array.isArray(resp) ? resp : Array.isArray(resp?.data) ? resp.data : []
        if (!Array.isArray(quotes) || quotes.length === 0) {
          hasError.value = true
          addQuoteError(resp)
          return
        }

        // const planId = getInsurancePlanId()
        // const quote =
        //   quotes.find((q) => String(q?.planId ?? '').toLowerCase() === planId.toLowerCase()) ?? quotes[0]
        const quote = quotes[0]
        quoteInfo.planId = planId = quote.planId
        for (let idx = 0; idx < group.length; idx++) {
          const customer = group[idx]
          const quoteRow = quotes.find((q: any) => String(q?.uniqueId ?? '') === String(customer?.travelerId ?? '')) ?? quotes[idx] ?? {}
          // Arch quotes/charges in USD regardless; when the backend adds a *Display pair
          // (converted to the page's currency), prefer it over the raw USD total so unPay
          // itself already reflects the display currency - no separate Display field needed.
          const previousTotal = toFiniteNumber(quoteRow.previousTotal, 0)
          const total = toFiniteNumber(quoteRow.total, 0)
          const policyDelta = quoteRow.wasFreeChild ? 0 : total - previousTotal

          const previousTotalDisplay = toFiniteNumber(quoteRow.previousTotalDisplay??quoteRow.previousTotal, 0)
          const totalDisplay = toFiniteNumber(quoteRow.totalDisplay??quoteRow.total, 0)
          const policyDeltaDisplay = quoteRow.wasFreeChild ? 0 : totalDisplay - previousTotalDisplay


          customer.insuranceQuote = {
            age: customer.age, // policyPurchase (CommitPolicyChanges) reads insuranceQuote.age
            individualPolicyCost: Math.max(0, policyDelta),
            individualPolicyCostDisplay: Math.max(0, policyDeltaDisplay),
            // Signed delta (can be negative). individualPolicyCost clamps to >=0 for the charge,
            // netPolicyCost preserves the sign so a decrease isn't mistaken for a no-op change.
            netPolicyCost: policyDelta,
            individualTripCost: toFiniteNumber(quoteRow.totalTripCost, 0),
            individualTripCostDisplay: toFiniteNumber(quoteRow.totalTripCostDisplay??quoteRow.totalTripCost, 0),
            previous:previousTotal,
            previousDisplay:previousTotalDisplay,
            displayCurrency: quoteRow.displayCurrency
          }
        }
      }
      else{
        const resp = await InsuranceService.getQuotes({
          returnIndividualTravelerPricingDetails: true,
          insuredPerson: group.map((c) => buildInsuredFromCustomer(c)),
          coverage: coverage
        }, displayCurrency)

        if (resp?.success != null && resp.success == false) {
          const responseMessage = String(resp?.responseMessage ?? resp?.message ?? '')
          if (group.length > 1 && /traveler age limit exceeded/i.test(responseMessage)) {
            await Promise.all(group.map((customer) => getGroupQuote([customer], coverage, {})))
            return
          }
          hasError.value = true
          addQuoteError(resp)
          return
        }

        const quotes = Array.isArray(resp) ? resp : Array.isArray(resp?.data) ? resp.data : []
        if (!Array.isArray(quotes) || quotes.length === 0) {
          hasError.value = true
          addQuoteError(resp)
          return
        }

        // const planId = getInsurancePlanId()
        // const quote =
        //   quotes.find((q) => String(q?.planId ?? '').toLowerCase() === planId.toLowerCase()) ?? quotes[0]
        const quote = quotes[0]
        quoteInfo.planId = planId = quote.planId
        const individualQuotes = Array.isArray(quote?.insuredPerson) ? (quote.insuredPerson as any[]) : []
        const sortedGroup = [...group].sort((a, b) => (b?.age ?? 0) - (a?.age ?? 0))
        for (const idx in sortedGroup) {
          const customer = sortedGroup[idx]
          customer.insuranceQuote = individualQuotes[idx] ?? null
         // console.log('---insurance---customer----',customer)
        }
      }
    } catch (err) {
      hasError.value = true
      addQuoteError(err)
    }
  }

  function chunkArray(arr, size) {
    const result = []
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size))
    }
    return result
  }

  async function queryQuotes(previousInsurance) {
    hasError.value = false
    quoteErrors.value = []
    const coverage = {
      depositDate: getCurrentDate(),
      effectiveDate: quoteInfo.coverage.effectiveDate,
      expirationDate: quoteInfo.coverage.expirationDate,
      destinations: quoteInfo.coverage.destinations
    }

    // const adultGroup = quoteInfo.customers.filter((c) => c.ageGroup == 'adult')
    // const childGroup = quoteInfo.customers.filter((c) => c.ageGroup == 'child')

    // previousInsurance may only cover SOME of quoteInfo.customers (e.g. a traveler added after
    // the original purchase, or one who was declined). insuredTravelerIds (set by orderRoomlist)
    // tells us who's actually covered, so those go through the policy-update quote and everyone
    // else gets a fresh new-purchase quote instead of being requoted as an "update" too.
    const insuredTravelerIds = Array.isArray(previousInsurance?.insuredTravelerIds)
      ? new Set(previousInsurance.insuredTravelerIds.map((id: any) => String(id)))
      : null

    const groups = insuredTravelerIds
      ? [
          quoteInfo.customers.filter((c) => insuredTravelerIds.has(String(c.travelerId ?? ''))),
          quoteInfo.customers.filter((c) => !insuredTravelerIds.has(String(c.travelerId ?? '')))
        ]
      : [quoteInfo.customers]
    const previousInsuranceByGroup = insuredTravelerIds ? [previousInsurance, {}] : [previousInsurance]

    await Promise.all(groups.map((group, idx) => getGroupQuote(group, coverage, previousInsuranceByGroup[idx])))
    if (quoteInfo.customers.some((customer) => customer.insuranceQuote != null)) {
      hasError.value = false
    }
    // await getGroupQuote(adultGroup, coverage)
    // await getGroupQuote(childGroup, coverage)
    refreshTotals()
  }

  async function buildQuoteInfo(order: any, prices: any, rooms: any) {
    const cacheKey = getQuoteInfoCacheKey()
    const storedData = await localDriver.getItem(cacheKey)
    // Room/trip price can change out from under a cached quote (e.g. the 20-minute
    // price-hold check finds a new price and updates prices_info before landing back
    // here). The cache below only patches flightPrice, so a trip-price-only change was
    // silently ignored - detect it and fall through to a full rebuild instead of reusing
    // stale customer.tripPrice values.
    const currentBasePrice = prices?.roompriceDetails?.[0]?.price ?? null
    const tripPriceChanged = storedData?.planId != null && currentBasePrice != null &&
      Number(storedData.sourceTripPrice) !== Number(currentBasePrice)

    if (storedData?.planId && !tripPriceChanged) {
      //console.log('===get cache info====')
      planId = storedData.planId
      Object.assign(quoteInfo, { ...storedData })

      const travelerCount = Array.isArray(rooms)
        ? rooms.reduce((count, room) => count + (parseInt(room.Adults) || 0) + (parseInt(room.Child) || 0), 0)
        : 0
      const hasCurrentFlightPrice = order != null && (
        Object.prototype.hasOwnProperty.call(order, 'flights_amount') || order.flightOffer != null
      )
      const totalFlightPrice = getInsuranceFlightTotal(order)
      const flightPricePerPerson = travelerCount > 0 ? totalFlightPrice / travelerCount : 0
      const flightPriceChanged = hasCurrentFlightPrice && travelerCount > 0 &&
        quoteInfo.customers.some((customer) => Number(customer.flightPrice || 0) !== flightPricePerPerson)

      // A flight change moves the trip dates (odata.real_depart_date / real_arrival_date are set
      // from the new itinerary). The cached coverage still held the old flight's dates, so the
      // policy update was committed for the old period (order 26482: 12-07..12-14 instead of
      // 12-05..12-17). Only plain 'YYYY-MM-DD...' strings are trusted here.
      const orderDate = (value: any) =>
        typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null
      const currentDepart = orderDate(order?.real_depart_date)
      const currentReturn = orderDate(order?.real_arrival_date)
      const datesChanged = currentDepart != null && currentReturn != null && (
        orderDate(quoteInfo.coverage.effectiveDate) !== currentDepart ||
        orderDate(quoteInfo.coverage.expirationDate) !== currentReturn
      )
      if (datesChanged) {
        quoteInfo.coverage.effectiveDate = currentDepart
        quoteInfo.coverage.expirationDate = currentReturn
      }

      if (flightPriceChanged || datesChanged)
      {
        if (flightPriceChanged) {
          quoteInfo.customers.forEach((customer) => { customer.flightPrice = flightPricePerPerson })
        }
        quoteInfo.totalTripCost = quoteInfo.customers.reduce(
          (total, customer) => total + Number(customer.tripPrice || 0) + Number(customer.soloPrice || 0) +
            Number(customer.optionsPrice || 0) + Number(customer.flightPrice || 0),
          0
        )
        await queryQuotes(order?.insurance ?? [])
        await localDriver.setItem(cacheKey, quoteInfo)
      }
      else{
        refreshTotals()
      }
      return
    }
    if (storedData != null){
       await removeAllQuoteInfoCache()
    }
    // const withFlight = configs.withFlight == '1' ? true : false
    const withFlight = true
    if (order == null || typeof order !== 'object') {
      hasError.value = true
      return
    }

    if (rooms == null || !Array.isArray(rooms) || rooms.length === 0) {
      hasError.value = true
      return
    }

    const departDate = order?.real_depart_date ?? null
    const returnDate = order?.real_arrival_date ?? null
    if (departDate == null || returnDate == null) {
      hasError.value = true
      return
    }

    const productModel = String(order?.product_info?.model ?? '')
    if (productModel.trim() === '') {
      hasError.value = true
      return
    }

    const customerMap = Object.create(null)
    const customerKeysByName = Object.create(null)
    const usedCustomerKeys = new Set<string>()
    const productOptionMap = Object.create(null)

    order?.oriOptions?.forEach((opt) => {
      Object.values(opt.product_option_value).forEach((o: any) => {
        productOptionMap[o.product_option_value_id] = o
      })
    })

    const soloRoomPrice = prices?.roompriceDetails?.[1]?.price ?? 0

    function getFullName(customer) {
      return formatName(
        customer.firstname,
        notEmpty(customer.middlename) ? customer.middlename : '',
        customer.lastname
      )
    }
    function buildCustomerKey(customer: any, fallback: string) {
      const uid = String(customer?.travelerUid ?? customer?.traveler_uid ?? '').trim()
      const baseKey = uid !== '' ? uid : fallback
      if (!usedCustomerKeys.has(baseKey)) {
        usedCustomerKeys.add(baseKey)
        return baseKey
      }
      let suffix = 2
      let nextKey = `${baseKey}#${suffix}`
      while (usedCustomerKeys.has(nextKey)) {
        suffix += 1
        nextKey = `${baseKey}#${suffix}`
      }
      usedCustomerKeys.add(nextKey)
      return nextKey
    }

    // const tripPrice = prices.roompriceDetails[0].price
    //here tba ???
    const totalFlightPrice = getInsuranceFlightTotal(order)

    //here tba ???
    const totalCustomerCount = rooms
      .map((r) => parseInt(r.Adults) + parseInt(r.Child))
      .reduce((acc, val) => acc + val)
    if (totalCustomerCount <= 0) {
      hasError.value = true
      return
    }
    const filghtPricePerPerson = withFlight ? totalFlightPrice / totalCustomerCount : 0
    const flightAdditionPrice =
      withFlight && (prices?.fly ?? null) != null ? prices.fly.price / totalCustomerCount : 0
    const isFp = productModel.toUpperCase().slice(0, 3) === 'FP-'
    let tripPrice = prices?.roompriceDetails?.[0]?.price ?? 0
    if (!isFp) {
      tripPrice -= 0 - flightAdditionPrice
    } else {
      tripPrice -= filghtPricePerPerson - flightAdditionPrice
    }
    // tripPrice =
    //   prices.roompriceDetails[0].price - (filghtPricePerPerson - flightAdditionPrice)

    rooms.forEach((room, roomIdx) => {
      const soloRoom = (parseInt(room.Adults) + parseInt(room.Child) == 1)
      if (room.Adultslist != null) {
        room.Adultslist.forEach((customer, travelerIdx) => {
          const name = getFullName(customer)
          const soloPrice = soloRoom ? soloRoomPrice : 0
          tripPrice = prices?.travelerPrice?.[name]??tripPrice
          const customerKey = buildCustomerKey(customer, `adult:${roomIdx}:${travelerIdx}:${name}`)
          customerMap[customerKey] = {
            ...customer,
            room: room.name,
            soloRoom,
            tripPrice,
            soloPrice,
            optionsPrice: 0,
            age: calculateAge(customer.Birthday??customer.birthday),
            ageGroup: 'adult',
            accept: null,
            flightPrice: filghtPricePerPerson
          }
          if (customerKeysByName[name] == null) customerKeysByName[name] = []
          customerKeysByName[name].push(customerKey)
        })
      }
      if (room.Childlist != null) {
        room.Childlist.forEach((customer, travelerIdx) => {
          const name = getFullName(customer)
          const soloPrice = soloRoom ? soloRoomPrice : 0
          tripPrice = prices?.travelerPrice?.[name]??tripPrice
          const customerKey = buildCustomerKey(customer, `child:${roomIdx}:${travelerIdx}:${name}`)
          customerMap[customerKey] = {
            ...customer,
            room: room.name,
            soloRoom,
            tripPrice,
            soloPrice,
            optionsPrice: 0,
            age: calculateAge(customer.Birthday??customer.birthday),
            ageGroup: 'child',
            accept: null,
            flightPrice: filghtPricePerPerson
          }
          if (customerKeysByName[name] == null) customerKeysByName[name] = []
          customerKeysByName[name].push(customerKey)
        })
      }
    })

    const orderOptions = order?.options ?? {}
    for (const productOptionId in orderOptions) {
      const customerRefs = orderOptions[productOptionId]
      const option = productOptionMap[productOptionId]
      if (option == null || !Array.isArray(customerRefs)) {
        continue
      }
      customerRefs.forEach((customerRef) => {
        const keyOrName = String(customerRef ?? '').trim()
        if (keyOrName === '') return
        const customer =
          customerMap[keyOrName] ??
          customerMap[(customerKeysByName[keyOrName] ?? [])[0]]
        if (null == customer) {
          pagelog(new Request(), 'insurance_data_error', {
            order: order,
            customerRef: keyOrName,
            customers: customerMap
          })
          return
        }
        if (customer.optionsPrice === undefined) customer.optionsPrice = 0
        if (option.price_prefix === '-') {
          customer.optionsPrice -= Number.parseFloat(option.price)
        } else {
          customer.optionsPrice += Number.parseFloat(option.price)
        }
      })
    }

    quoteInfo.customers = Object.values(customerMap).map((c) => {
      // The saved-traveler list also carries the product, departure date and order of the traveler's
      // earlier bookings (used only to flag travelers already booked on this trip). They are not part of
      // this order, so keep them out of the quote and purchase request.
      const customer: any = { ...(c as any) }
      for (const key of ['product_id', 'depart_date', 'order_id', 'order_status_id']) {
        delete customer[key]
      }
      return reactive(customer)
    })

    const countryIds = quoteInfo.customers.map((c) => getCountryIdFromZoneId(c.resident_of))
    const zoneIds = quoteInfo.customers
      .map((c) => getStateIdFromZoneId(c.resident_of))
      .filter((z) => z != null && '' != z.trim())

    const codesMap = []  ///await InsuranceService.getCountryAndZoneCodes(countryIds, zoneIds)
    quoteInfo.customers.forEach((c) => {
      const countryId = getCountryIdFromZoneId(c.resident_of)
      const stateId = getStateIdFromZoneId(c.resident_of)

      if (countryId != null) {
        c.countryAbbreviation = codesMap?.['countryMap']?.[countryId]?.['iso_code_2'] ?? countryId
      }
      c.stateAbbreviation = (stateId != null && '' != stateId.trim())?(codesMap?.['zoneMap']?.[stateId]?.['code'] ?? stateId):c.resident_of
      if ('US' === c.countryAbbreviation && 'AS' === c.stateAbbreviation) {
        c.countryAbbreviation = 'AS'
        c.stateAbbreviation = null
      }
    })

    quoteInfo.coverage.effectiveDate = departDate
    quoteInfo.coverage.expirationDate = returnDate
    const allDays = Array.isArray(order?.alldays) ? order.alldays : []
    const countryCodes = await InsuranceService.getCountryCodesByCityIds(
      allDays.map((d) => d.cityid).filter((it) => it != null && it != '')
    )
    quoteInfo.totalTripCost = quoteInfo.customers
      .map((c) => c.tripPrice + c.soloPrice + c.optionsPrice + c.flightPrice)
      .reduce((acc, v) => acc + v)
    // Recorded so a later buildQuoteInfo() call can detect a trip/room price change and
    // rebuild instead of reusing this cached quote's now-stale customer.tripPrice values.
    quoteInfo.sourceTripPrice = prices?.roompriceDetails?.[0]?.price ?? null
    quoteInfo.coverage.destinations = toPrimaryDestination(countryCodes)

    try {
      await queryQuotes(order.insurance??[])
      // console.log('===get api info====')
    } catch (err) {
      hasError.value = true
      throw err
    }
  }

  function needPurchase(): boolean {
    for (const c of quoteInfo.customers) {
      if (c.accept == true) {
        return true
      }
    }
    return false
  }

  async function reset() {
    //console.log('===reset====')
    quoteInfo.coverage = {
      effectiveDate: null,
      expirationDate: null,
      destinations: [] as { countryCode: string }[]
    }
    quoteInfo.customers = []
    quoteInfo.totalTripCost = 0
    quoteInfo.planId = planId
    await removeAllQuoteInfoCache()
    insuranceOrders.value = []
    unPay.value = 0
    hasError.value = false
    quoteErrors.value = []
  }

  async function purchase(
    orderId: number,
    address: {
      address1: string
      postalCode: string
      city: string
      stateAbbreviation: string
      countryAbbreviation: string
    },
    paymentInfo: {
      cardHolderName: string
      cardNumber: string
      cvc: string
      expirationMonth: string
      expirationYear: string
    },
    at: 'booking' | 'customerCenter' = 'booking'
  ) {
    const validInsurance = await validateInsurancePlan()
    if (!validInsurance) {
      return
    }

    // const planId = getInsurancePlanId()
    quoteInfo.planId = planId

    paymentInfo.cardNumber = paymentInfo.cardNumber.replace(/\s+/g, '')

    const insItems = []
    for (const c of quoteInfo.customers) {
      if (c.accept == true) {
        insItems.push(c)
      }
    }

    if (insItems.length == 0) {
      return
    }

    const groups = [insItems]

    const results = Promise.all(
      groups.map(async (group) => {
        const total = group
          .map((ins) => paymentInfo
            ? ins.insuranceQuote.individualPolicyCost
            : (ins.insuranceQuote.netPolicyCost ?? ins.insuranceQuote.individualPolicyCost))
          .reduce((acc, val) => {
            return acc + val
          })

        const purchase = {
          planId,
          total: total,
          purchasedDate: new Date().toISOString(),
          insuredPerson: group.map((c) => {
            const customerAddress = {
              ...address, ...{
                countryAbbreviation: c.countryAbbreviation,
                stateAbbreviation: c.stateAbbreviation || address.stateAbbreviation
              }
            }
            return {
              uniqueId: c.travelerId,
              firstName: c.firstname,
              lastName: c.lastname,
              middleName: c.middlename,
              dob: c.Birthday??c.birthday,
              age: c.insuranceQuote.age,
              emailAddress: c.Email,
              address: customerAddress,
              individualTripCost: c.insuranceQuote.individualTripCost,
              individualPolicyCost: paymentInfo
                ? c.insuranceQuote.individualPolicyCost
                : (c.insuranceQuote.netPolicyCost ?? c.insuranceQuote.individualPolicyCost)
            }
          }),
          buyer: {
            firstName: insItems[0].firstname,
            lastName: insItems[0].lastname,
            middleName: insItems[0].middlename
          },
          coverage: { ...quoteInfo.coverage, depositDate: getCurrentDate() },
          payment: {
            payWithCreditCard: { ...paymentInfo }
          }
        }
        const request = {
          orderId: orderId,
          at: at,
          quoteInfo: buildRawQuoteInfoForSubmit(planId),
          purchase: purchase
        }
        const result = await InsuranceService.purchaseQuotes(request)
        return result
      })
    )
    return results
  }

  async function policyPurchase(
    orderId: number,
    address: {
      address1: string
      postalCode: string
      city: string
      stateAbbreviation: string
      countryAbbreviation: string
    },
    paymentInfo: {
      cardHolderName: string
      cardNumber: string
      cvc: string
      expirationMonth: string
      expirationYear: string
    } | null,
  ) {
    const cancellations = paymentInfo ? [] : pendingPolicyCancellations
    const validInsurance = quoteInfo.customers.length > 0 ? await validateInsurancePlan() : cancellations.length > 0
    if (!validInsurance) {
      return
    }

    // const planId = getInsurancePlanId()
    if(planId)
    {
      quoteInfo.planId = planId
    }
    else{
      planId = quoteInfo.planId
    }

    // paymentInfo is null for a coverage-only change (no additional premium): commit the change
    // WITHOUT a payment block (Arch doesn't require payment when nothing is due).
    if (paymentInfo) {
      paymentInfo.cardNumber = paymentInfo.cardNumber.replace(/\s+/g, '')
    }

    const insItems = []
    for (const c of quoteInfo.customers) {
      if (c.accept == true) {
        insItems.push(c)
      }
    }

    if (insItems.length == 0) {
      return Promise.all(cancellations.map((cancel) => InsuranceService.cancelPolicy(cancel)))
    }

    const groups = [insItems]

    const results = await Promise.all([
      ...cancellations.map((cancel) => InsuranceService.cancelPolicy(cancel)),
      ...groups.map(async (group) => {
        const total = group
          .map((ins) => paymentInfo
            ? ins.insuranceQuote.individualPolicyCost
            : (ins.insuranceQuote.netPolicyCost ?? ins.insuranceQuote.individualPolicyCost))
          .reduce((acc, val) => {
            return acc + val
          })

        const purchase = {
          planId,
          total: total,
          purchasedDate: new Date().toISOString(),
          insuredPerson: group.map((c) => {
            const customerAddress = {
              ...address, ...{
                countryAbbreviation: c.countryAbbreviation,
                stateAbbreviation: c.stateAbbreviation || address.stateAbbreviation
              }
            }
            return {
              uniqueId: c.travelerId,
              firstName: c.firstname,
              lastName: c.lastname,
              middleName: c.middlename,
              dob: c.Birthday??c.birthday,
              age: c.insuranceQuote.age,
              emailAddress: c.Email,
              address: customerAddress,
              individualTripCost: c.insuranceQuote.individualTripCost,
              individualPolicyCost: paymentInfo
                ? c.insuranceQuote.individualPolicyCost
                : (c.insuranceQuote.netPolicyCost ?? c.insuranceQuote.individualPolicyCost)
            }
          }),
          buyer: {
            firstName: insItems[0].firstname,
            lastName: insItems[0].lastname,
            middleName: insItems[0].middlename
          },
          coverage: { ...quoteInfo.coverage, depositDate: getCurrentDate() },
          ...(paymentInfo ? { payment: { payWithCreditCard: { ...paymentInfo } } } : {})
        }
        const request = {
          orderId: orderId,
          insuranceId: lastUpdatingInsurance?.id,
          insuranceOrderNumber: lastUpdatingInsurance?.ins_order_number,
          quoteInfo: buildRawQuoteInfoForSubmit(planId),
          purchase: purchase
        }
        const result = await InsuranceService.updatePurchaseQuotes(request)
        return result
      })
    ])
    return normalizeInsurancePolicyResults(results)
  }

  async function validateInsurancePlan(): Promise<boolean> {
    if (skip.value) return true

    if (quoteInfo.customers == null || quoteInfo.customers.length === 0) {
      pagelog(new Request(), 'insurance_data_error', {err:'nocustomer'})
      return false
    }

    for (const c of quoteInfo.customers) {
      if (c.accept == null) {
        pagelog(new Request(), 'insurance_data_error', {err:'no accept',c:c})
        return false
      }
    }
    planId = planId || quoteInfo.planId
    if (planId == null) {
      pagelog(new Request(), 'insurance_data_error', {err:'null planid'})
      return false
    }
    quoteInfo.planId = planId
    const cacheKey = getQuoteInfoCacheKey()
    await localDriver.setItem(cacheKey, quoteInfo)
    return true
  }

  const insuranceOrders = ref([])

  // ponytail: cap polling so a stuck status=2 order (e.g. provider never responds, no
  // check_pending_orders cron) can't poll get_by_order_id forever. ~12 tries / 1 min.
  const POLL_MAX_ATTEMPTS = 12
  let pollTimer: ReturnType<typeof setInterval> | null = null

  async function watchPendingOrders(orderId: any) {
    if (pollTimer != null) clearInterval(pollTimer) // never stack intervals
    let attempts = 0
    pollTimer = setInterval(async () => {
      attempts++
      try {
        const response: unknown = await InsuranceService.getByOrderId(orderId)
        const data = typeof response === 'object' && response != null && 'data' in response ? response.data : null
        const orders = Array.isArray(response) ? response : Array.isArray(data) ? data : null
        if (orders == null) throw new Error('Invalid insurance order response')
        insuranceOrders.value = orders
        if (orders.some((it) => it.status == '2') && attempts < POLL_MAX_ATTEMPTS) return
      } catch (err) {
        console.error('Failed to refresh pending insurance orders:', err)
      }

      if (pollTimer != null) {
        clearInterval(pollTimer)
        pollTimer = null
      }
    }, 5000)
  }

  const isDateValid = ref(true)

  // Current order info (real travel dates) from the last buildQuoteInfoFromOrder, reused when
  // starting a policy update so the re-quote carries the CURRENT trip dates.
  let lastOrderInfo: any = null
  let lastUpdatingInsurance: any = null
  let pendingPolicyCancellations: any[] = []

  // Build quoteInfo.customers from a list of order travelers (shared by new-purchase and update
  // flows). `accept` seeds whether each traveler is opted-in (update = true, new-purchase = false).
  async function buildCustomersFromTravelers(travelers: any[], flightPrice: number, accept: boolean) {
    const codesMap = await InsuranceService.getCountryAndZoneCodes(
      travelers.map((t) => getCountryIdFromZoneId(t.traveler.zone_id)),
      travelers
        .map((t) => getStateIdFromZoneId(t.traveler.zone_id))
        .filter((z) => z != null && '' != z.trim())
    )

    for (const t of travelers) {
      const countryId = getCountryIdFromZoneId(t.traveler.zone_id)
      const stateId = getStateIdFromZoneId(t.traveler.zone_id)
      const c = {
        firstname: t.traveler.firstname,
        travelerId: t.traveler_id ?? t.traveler.traveler_id,
        middlename: t.traveler.middlename,
        lastname: t.traveler.lastname,
        Birthday: t.traveler.birthday ?? t.traveler.Birthday,
        accept: accept,
        age: calculateAge(t.traveler.birthday ?? t.traveler.Birthday),
        tripPrice: t.price,
        soloPrice: 0,
        flightPrice: flightPrice,
        optionsPrice: t.option_price ? t.option_price : 0,
        countryAbbreviation:
          codesMap['countryMap'] &&
            codesMap['countryMap'][countryId] &&
            codesMap['countryMap'][countryId]['iso_code_2'] !== undefined
            ? codesMap['countryMap'][countryId]['iso_code_2']
            : '',
        stateAbbreviation:
          stateId != null && stateId != 0 ? codesMap['zoneMap'][stateId]['code'] : null
      }

      if ('US' === c.countryAbbreviation && 'AS' === c.stateAbbreviation) {
        c.countryAbbreviation = 'AS'
        c.stateAbbreviation = null
      }
      quoteInfo.customers.push(c)
    }
  }

  // Prepare an update for one or more already-purchased policies: rebuild quoteInfo from ALL
  // affected policies' insured travelers (with current prices, opted-in), set the CURRENT travel
  // dates, and re-quote the difference via QuotePolicyChanges (getGroupQuote takes the update
  // branch when previousInsurance has an id). Both QuotePolicyChanges and CommitPolicyChanges
  // resolve every active policy for the order_id server-side (matched by insured person), so
  // merging every updatable order's travelers into one call quotes/commits them all together.
  // Returns the additional premium so the caller can collect payment then commit via
  // policyPurchase (CommitPolicyChanges).
  async function startPolicyUpdate(orders: any | any[]): Promise<{ success: boolean; diff: number; net?: number }> {
    hasError.value = false
    const orderList = (Array.isArray(orders) ? orders : [orders]).filter(Boolean)
    if (orderList.length === 0) {
      hasError.value = true
      return { success: false, diff: 0 }
    }
    lastUpdatingInsurance = orderList[0]
    pendingPolicyCancellations = []
    const orderId = orderList[0].order_id
    quoteInfo.customers = []
    unPay.value = 0

    const data = await InsuranceService.getInfoListByOrder(orderId)
    const flightPrice = data.flight && data.flight.price > 0 ? data.flight.price : 0

    const norm = (o: any) =>
      `${(o?.firstName ?? o?.firstname ?? '').toString().trim().toUpperCase()}|${(o?.lastName ?? o?.lastname ?? '').toString().trim().toUpperCase()}`
    const currentTravelerIds = new Set((data.travelers as any[]).map((t) => String(t?.traveler_id ?? t?.traveler?.traveler_id ?? '')).filter(Boolean))

    // Union of insured travelers across every policy being updated together.
    const insuredIds = new Set<string>()
    const insuredKeys = new Set<string>()
    const cancellations: any[] = []
    for (const order of orderList) {
      const persons =
        (Array.isArray(order?.raw_purchase?.insuredPerson) && order.raw_purchase.insuredPerson.length > 0)
          ? order.raw_purchase.insuredPerson
          : (order?.raw_response?.coverageDetail ?? [])
      for (const p of persons) {
        const id = String(p?.uniqueId ?? '')
        if (id !== '') {
          insuredIds.add(id)
        } else {
          insuredKeys.add(norm(p))
        }
      }
      const details = Array.isArray(order?.raw_response?.coverageDetail) ? order.raw_response.coverageDetail : []
      cancellations.push(
        ...persons
          .filter((p: any) => p?.uniqueId != null && !currentTravelerIds.has(String(p.uniqueId)))
          .map((p: any) => {
            const detail = details.find((d: any) =>
              String(d?.uniqueId ?? '') === String(p.uniqueId) ||
              (norm(d) === norm(p) && d?.policyNumber))
            return {
              orderId,
              insuranceId: order.id,
              insuranceOrderNumber: order.ins_order_number,
              individualPolicyNumber: detail?.policyNumber ?? p?.individualPolicyNumber ?? '',
              amount: Number(p?.individualPolicyCost ?? detail?.total ?? 0)
            }
          })
          .filter((p: any) => p.individualPolicyNumber && p.amount > 0)
      )
    }
    pendingPolicyCancellations = cancellations

    const travelers = (data.travelers as any[]).filter((t) => {
      const travelerId = String(t?.traveler_id ?? t?.traveler?.traveler_id ?? '')
      return (travelerId !== '' && insuredIds.has(travelerId)) || insuredKeys.has(norm(t.traveler))
    })
    if (travelers.length === 0 && pendingPolicyCancellations.length === 0) {
      hasError.value = true
      return { success: false, diff: 0 }
    }

    if (travelers.length > 0) {
      await buildCustomersFromTravelers(travelers, flightPrice, true)
      // Fetched fresh (not the lastOrderInfo cached at page load) so a stale in-memory copy can't
      // resubmit an outdated departure/return date once the order's dates have since shifted.
      const freshOrderInfo = await InsuranceService.getOrderRealDates(orderId)
      quoteInfo.coverage.effectiveDate = freshOrderInfo?.real_depart_date || data.dates.depart_date
      quoteInfo.coverage.expirationDate = freshOrderInfo?.real_return_date || data.dates.return_date
      const countryCodes = await InsuranceService.getCountryCodesByCityIds(data.dates.cities.split(','))
      quoteInfo.coverage.destinations = toPrimaryDestination(countryCodes)

      // previousInsurance only needs a truthy order_id/id to select the update branch server-side
      // reads across all active policies for order_id regardless of which one is passed here.
      await queryQuotes(orderList[0])
    }
    return { success: !hasError.value, diff: unPay.value, net: netUnPay.value }
  }

  // Rebuild the panel state after an aborted policy update (startPolicyUpdate clobbers
  // quoteInfo.customers, which policyPurchase needs; restore it if we don't commit).
  async function reloadInsurance(orderId: number) {
    if (lastOrderInfo != null) {
      await buildQuoteInfoFromOrder(orderId, lastOrderInfo)
    }
  }

  async function buildQuoteInfoFromOrder(orderId: number, orderInfo: any) {
    await reset()
    const MAX_DAYS = 22
    let data: any
    try {
      data = await InsuranceService.getInfoListByOrder(orderId)
    } catch (err) {
      hasError.value = true
      addQuoteError({ message: 'Insurance information is temporarily unavailable. Please refresh the page.' })
      console.error('Failed to load insurance order information:', err)
      return
    }
    if (data == null || typeof data !== 'object' || Array.isArray(data)) {
      hasError.value = true
      addQuoteError({ message: 'Insurance information is temporarily unavailable. Please refresh the page.' })
      console.error('Invalid insurance order information response')
      return
    }

    const dates = data.dates != null && typeof data.dates === 'object' ? data.dates : {}
    const insurances = Array.isArray(data.insurances) ? data.insurances : []
    const orderTravelers = Array.isArray(data.travelers) ? data.travelers : []
    const flight = data.flight != null && typeof data.flight === 'object' ? data.flight : {}
    // Feature switch (ltw_setting_ext: insurance_update_enabled). When off, never offer
    // a policy update — every row stays updateState='none'. Defaults to off if the backend
    // doesn't send the flag (e.g. not yet deployed).
    const updateEnabled = !!data.update_enabled

    const departDateValue = dates.depart_date || orderInfo?.real_depart_date || orderInfo?.products?.[0]?.depart_date || ''
    const departDate = new Date(departDateValue).getTime()
    const now = new Date().getTime()

    const diffDays = (departDate - now) / (1000 * 60 * 60 * 24)

    isDateValid.value = Number.isFinite(departDate) && diffDays > MAX_DAYS

    insuranceOrders.value = insurances
    if (insurances.length > 0) {
      const pendings = insurances.filter((it) => it.status == '2')
      if (pendings != null && pendings.length > 0) {
        watchPendingOrders(orderId)
      }
    }
    // Across all purchase records (which may share one insurance order number),
    // collect every insured person. Records that store raw_purchase without
    // insuredPerson expose the people under raw_response.coverageDetail instead.
    const extractInsured = (ins: any): any[] => {
      const fromPurchase = ins?.raw_purchase?.insuredPerson
      if (Array.isArray(fromPurchase) && fromPurchase.length > 0) {
        return fromPurchase
      }
      const fromResponse = ins?.raw_response?.coverageDetail
      if (Array.isArray(fromResponse) && fromResponse.length > 0) {
        return fromResponse.map((d: any) => ({
          uniqueId: d.uniqueId ?? null,
          firstName: d.firstName ?? '',
          middleName: d.middleName ?? '',
          lastName: d.lastName ?? ''
        }))
      }
      return []
    }
    const allInsured = insurances.flatMap(extractInsured) as any[]
    const isAlreadyInsured = (t: any): boolean =>
      allInsured.some(
        (i) =>
          (i?.uniqueId != null && i.uniqueId == t.traveler_id) ||
          (i?.firstName == t.traveler?.firstname &&
            i?.middleName == (t.traveler?.middlename ?? '') &&
            i?.lastName == t.traveler?.lastname)
      )
    // const configs = await InsuranceService.getConfigs()
    // const withFlight = configs.withFlight == '1' ? true : false
    const withFlight = true
    const travelers = orderTravelers.filter((t) => !isAlreadyInsured(t))
    let flightPrice = 0

    if (withFlight && flight.price > 0) {
      flightPrice = parseFloat(flight.price) || 0
    }

    // --- Policy-update eligibility ---
    // Offer an "Update Insurance" entry when the current trip date OR amount differs from what is
    // insured. Arch rule (per RoamRight): additional insurance after a booking change must be
    // completed no later than 10 days before departure, and once the trip has begun the policy can
    // no longer be altered.
    //
    // A policy that was already updated has BOTH the original order_insurance row (old coverage) and
    // one/more update rows. So group all status=1 rows by Arch policy number (ins_order_number) and
    // compare against the LATEST committed coverage of the group (an update row records the new
    // coverage in raw_quote_info.updates[].coverage) — otherwise the stale original row would keep
    // the button visible even after a successful update.
    const UPDATE_DEADLINE_DAYS = 10
    const curDepart = (orderInfo?.real_depart_date || dates.depart_date || orderInfo?.products?.[0]?.depart_date || '').toString().slice(0, 10)
    const curReturn = (orderInfo?.real_return_date || dates.return_date || orderInfo?.products?.[0]?.return_date || '').toString().slice(0, 10)
    const daysToDepart = (new Date(curDepart).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    const currentCostByTraveler: Record<string, number> = {}
    for (const t of orderTravelers) {
      currentCostByTraveler[String(t.traveler_id)] =
        (parseFloat(t.price) || 0) + (parseFloat(t.option_price) || 0) + (withFlight ? flightPrice : 0)
    }

    const rowSeq = (ins: any) => Number(ins?.id ?? 0) // newest row wins within a policy group
    const groups: Record<string, any[]> = {}
    for (const ins of insurances) {
      ins.updateState = 'none'
      if (ins.status != '1') continue
      const key = String(ins.ins_order_number ?? ins.id)
      ;(groups[key] = groups[key] || []).push(ins)
    }

    if (updateEnabled) for (const key of Object.keys(groups)) {
      const rows = groups[key].slice().sort((a, b) => rowSeq(a) - rowSeq(b))
      // Insured names: from whichever row carries them (original raw_purchase/raw_response).
      const namesRow = rows.find((r) => extractInsured(r).length > 0)
      const insuredPeople = namesRow ? extractInsured(namesRow) : []
      if (insuredPeople.length === 0) continue
      const cancellationPending = insuredPeople.some((person) =>
        person?.uniqueId != null && currentCostByTraveler[String(person.uniqueId)] == null)
      if (!cancellationPending && !insuredPeople.some((person) => currentCostByTraveler[String(person.uniqueId)] != null)) continue

      // Latest committed coverage: prefer Arch's committed policy summary from
      // CommitPolicyChanges. raw_quote_info is only the pre-commit quote and can be
      // stale/incomplete after a successful update.
      const committedCoverage = getMergedCommittedInsuranceCoverage(rows, namesRow)
      const coveredDepart = committedCoverage.depart
      const coveredReturn = committedCoverage.returnDate
      const coveredCost = committedCoverage.cost

      // Traveler prices are in the order currency, Arch's covered tripCost is in USD: convert with
      // the newest row's currencyValue (insurance -> order currency rate snapshot) before comparing.
      const rateRow = rows.slice().reverse().find((r) => parseFloat(r?.currencyValue) > 0)
      const insuranceRate = rateRow ? parseFloat(rateRow.currencyValue) : 1
      const currentCost = insuredPeople.reduce(
        (s, person) => s + (currentCostByTraveler[String(person.uniqueId)] ?? 0), 0) / insuranceRate
      const dateChanged = committedCoverage.policies?.length
        ? committedCoverage.policies.some((policy) =>
          !!policy.depart && (policy.depart !== curDepart || policy.returnDate !== curReturn))
        : !!coveredDepart && (coveredDepart !== curDepart || coveredReturn !== curReturn)
      const amountChanged = Math.round(currentCost) !== Math.round(coveredCost)

      let state = 'none'
      if (dateChanged || amountChanged || cancellationPending) {
        if (!(daysToDepart > 0)) state = 'passed'                 // departure already passed -> hide
        else if (daysToDepart < UPDATE_DEADLINE_DAYS) state = 'contact' // past 10-day deadline -> call CS
        else state = 'available'                                  // >= 10 days before departure -> allow
      }
      // Attach the button to the row that carries the insured names (so onUpdateInsurance can read
      // its raw_purchase.insuredPerson); other rows in the group stay 'none' (no duplicate button).
      // Also carry WHY it changed + old/new dates so the panel can show a specific reason.
      if (namesRow) {
        namesRow.updateState = state
        namesRow.updateReason = {
          dateChanged,
          amountChanged,
          cancellationPending,
          oldDepart: coveredDepart,
          oldReturn: coveredReturn,
          newDepart: curDepart,
          newReturn: curReturn
        }
      }
    }
    // Confirm every candidate with the insurer's update quote, the same check the "Update Insurance"
    // action runs. Local date/amount comparison can flag a change that does not alter the premium
    // (and mixes display currency with the insurer's USD cost), so when the quote shows no additional
    // premium and no refund the prompt is hidden, matching the "No update needed" result.
    // A failed quote keeps the prompt so the customer can still try the update.
    if (updateEnabled) {
      for (const ins of insurances as any[]) {
        if (ins.updateState !== 'available') continue
        const res = await startPolicyUpdate([ins])
        if (res.success && !(res.diff > 0) && !((res.net ?? 0) < 0)) {
          ins.updateState = 'none'
          ins.updateReason = undefined
        }
      }
      // startPolicyUpdate writes quote state; clear it before the panel's own quote is built below.
      quoteInfo.customers = []
      unPay.value = 0
      netUnPay.value = 0
      pendingPolicyCancellations = []
      hasError.value = false
      quoteErrors.value = []
    }
    insuranceOrders.value = [...insurances] // new reference triggers updatableOrders after flags are added

    lastOrderInfo = orderInfo // cache for startPolicyUpdate (current real travel dates)
    await buildCustomersFromTravelers(travelers, flightPrice, false)
    // quoteInfo.coverage.effectiveDate = data.dates.depart_date
    // quoteInfo.coverage.expirationDate = data.dates.return_date
    quoteInfo.coverage.effectiveDate = curDepart
    quoteInfo.coverage.expirationDate = curReturn
    const countryCodes = await InsuranceService.getCountryCodesByCityIds(
      String(dates.cities ?? '').split(',').filter(Boolean)
    )
    quoteInfo.coverage.destinations = toPrimaryDestination(countryCodes)
    await queryQuotes([])
  }
  return {
    quoteInfo,
    insuranceOrders,
    unPay,
    unPayDisplay,
    paid,
    totalPrice,
    isDateValid,
    hasError,
    quoteErrors,
    skip,
    setSkip,
    purchase,
    policyPurchase,
    changeAccept,
    declineAll,
    buildQuoteInfo,
    buildQuoteInfoFromOrder,
    startPolicyUpdate,
    reloadInsurance,
    reset,
    needPurchase,
    queryQuotes,
    validateInsurancePlan
  }
})

export function getInsuranceFlightTotal(order: any): number {
  const persons = Number(order?.flightOffer?.persons)
  if (Number(order?.isFlight) > 0 && Number(order?.flightOffer?.id) > 0 && persons > 0) {
    return ((Number(order?.cityprice) || 0) + (Number(order?.flightOffer?.price) || 0)) * persons
  }
  return Number(order?.flights_amount) || 0
}

export function getInsuranceQuoteCacheKey(search = typeof window !== 'undefined' ? window.location.search : '') {
  const token = new URLSearchParams(search).get('tk')
  return token ? `insuranceQuoteInfo_${token}` : 'insuranceQuoteInfo'
}
